import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Bell,
  X,
  CheckCheck,
  Loader2,
  Clock,
  AlertTriangle,
  AlertCircle,
  ChevronRight,
  ArrowLeft,
  Calendar,
  Cpu,
  BellOff
} from 'lucide-react';
import {
  Notification,
  NotificationStage,
  stageColors
} from '../types/notification';
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead
} from '../services/api/notificationService';

// Poll interval: 5 minutes
const POLL_INTERVAL = 5 * 60 * 1000;

interface NotificationBellProps {
  userNetId: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ userNetId }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);

  // Fetch notifications
  const loadNotifications = useCallback(async () => {
    if (!userNetId) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await fetchNotifications(userNetId, 1, 20, false, true);
      setNotifications(response.items);
      setUnreadCount(response.unread_count);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  }, [userNetId]);

  // Initial load and polling
  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        bellRef.current &&
        !bellRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSelectedNotification(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle marking a single notification as read
  const handleMarkAsRead = async (notification: Notification) => {
    if (notification.read_at) return;

    try {
      await markNotificationAsRead(notification.id, userNetId);
      setNotifications(prev =>
        prev.map(n =>
          n.id === notification.id
            ? { ...n, read_at: new Date().toISOString(), read_by: userNetId }
            : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  // Handle marking all notifications as read
  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;

    try {
      setIsMarkingAllRead(true);
      await markAllNotificationsAsRead(userNetId, true);
      setNotifications(prev =>
        prev.map(n => ({
          ...n,
          read_at: n.read_at || new Date().toISOString(),
          read_by: n.read_by || userNetId
        }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification: Notification) => {
    handleMarkAsRead(notification);
    setSelectedNotification(notification);
  };

  // Format date for display
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format relative time
  const formatRelativeTime = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  // Get stage icon
  const getStageIcon = (stage: NotificationStage) => {
    if (stage === 'expired') {
      return <AlertCircle className="w-4 h-4" />;
    }
    if (stage === '7d' || stage === '14d') {
      return <AlertTriangle className="w-4 h-4" />;
    }
    return <Clock className="w-4 h-4" />;
  };

  // Get stage badge with icon
  const getStageBadge = (stage: NotificationStage, showIcon: boolean = true) => {
    const config = stageColors[stage];
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
        {showIcon && getStageIcon(stage)}
        {config.label}
      </span>
    );
  };

  // Get notification type icon
  const getTypeIcon = (notification: Notification) => {
    const isExpired = notification.notification_type === 'model_expired';
    const stage = notification.notification_stage;

    if (isExpired || stage === 'expired') {
      return (
        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
          <AlertCircle className="w-5 h-5 text-red-600" />
        </div>
      );
    }
    if (stage === '7d' || stage === '14d') {
      return (
        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
          <AlertTriangle className="w-5 h-5 text-orange-600" />
        </div>
      );
    }
    return (
      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
        <Clock className="w-5 h-5 text-blue-600" />
      </div>
    );
  };

  return (
    <div className="relative">
      {/* Bell Icon Button */}
      <button
        ref={bellRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-full transition-all duration-200 ${
          isOpen
            ? 'bg-blue-100 text-blue-600'
            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
        }`}
        aria-label="Notifications"
      >
        <Bell className={`w-6 h-6 ${unreadCount > 0 ? 'animate-pulse' : ''}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1 shadow-lg animate-bounce">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute right-0 mt-2 w-[420px] bg-white rounded-xl shadow-2xl border border-gray-100 z-50 max-h-[85vh] flex flex-col overflow-hidden"
          style={{
            animation: 'slideDown 0.2s ease-out',
          }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <Bell className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white text-lg">Notifications</h3>
                  {unreadCount > 0 && (
                    <p className="text-blue-100 text-xs">{unreadCount} unread</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    disabled={isMarkingAllRead}
                    className="text-sm text-white/90 hover:text-white flex items-center gap-1.5 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isMarkingAllRead ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCheck className="w-4 h-4" />
                    )}
                    <span>Mark all read</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    setIsOpen(false);
                    setSelectedNotification(null);
                  }}
                  className="text-white/70 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto flex-1 bg-gray-50">
            {isLoading && notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500 mb-3" />
                <p className="text-gray-500 text-sm">Loading notifications...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-3">
                  <AlertCircle className="w-6 h-6 text-red-500" />
                </div>
                <p className="text-red-600 font-medium mb-1">Failed to load</p>
                <p className="text-gray-500 text-sm mb-3">{error}</p>
                <button
                  onClick={loadNotifications}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline"
                >
                  Try again
                </button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4">
                  <BellOff className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-600 font-medium mb-1">No notifications</p>
                <p className="text-gray-400 text-sm text-center">
                  You're all caught up! We'll notify you<br />when there's something new.
                </p>
              </div>
            ) : selectedNotification ? (
              // Detail View
              <div className="p-5">
                <button
                  onClick={() => setSelectedNotification(null)}
                  className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium mb-4 group"
                >
                  <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                  Back to notifications
                </button>

                {/* Detail Card */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  {/* Card Header */}
                  <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-start gap-4">
                      {getTypeIcon(selectedNotification)}
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 text-lg mb-1">
                          {selectedNotification.kai_name}
                        </h4>
                        <div className="flex items-center gap-2">
                          {getStageBadge(selectedNotification.notification_stage)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="px-5 py-4 space-y-4">
                    {/* Info Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <Cpu className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Model</p>
                          <p className="text-sm font-medium text-gray-800">{selectedNotification.model_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <Calendar className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Expires</p>
                          <p className="text-sm font-medium text-gray-800">{formatDate(selectedNotification.model_expiry_date)}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Clock className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Notification Sent</p>
                        <p className="text-sm font-medium text-gray-800">{formatDate(selectedNotification.sent_at)}</p>
                      </div>
                    </div>

                    {/* Email Content */}
                    {selectedNotification.email_subject && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Email Details</p>
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                          <p className="font-medium text-gray-800 mb-2">
                            {selectedNotification.email_subject}
                          </p>
                          {selectedNotification.email_body && (
                            <div
                              className="text-sm text-gray-600 prose prose-sm max-w-none"
                              dangerouslySetInnerHTML={{ __html: selectedNotification.email_body }}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // List View
              <div className="divide-y divide-gray-100">
                {notifications.map(notification => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`px-5 py-4 cursor-pointer transition-all duration-200 hover:bg-white group ${
                      !notification.read_at
                        ? 'bg-blue-50/70 border-l-4 border-l-blue-500'
                        : 'bg-white border-l-4 border-l-transparent'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      {getTypeIcon(notification)}

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <p className={`font-semibold truncate ${!notification.read_at ? 'text-gray-900' : 'text-gray-700'}`}>
                            {notification.kai_name}
                          </p>
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            {formatRelativeTime(notification.sent_at)}
                          </span>
                        </div>

                        <div className="flex items-center gap-2 mb-2">
                          {getStageBadge(notification.notification_stage, false)}
                          <span className="text-xs text-gray-500">
                            {notification.notification_type === 'model_expired' ? 'Model Expired' : 'Expiry Warning'}
                          </span>
                        </div>

                        <p className="text-sm text-gray-500 flex items-center gap-1.5">
                          <Cpu className="w-3.5 h-3.5" />
                          {notification.model_name}
                          <span className="mx-1">·</span>
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(notification.model_expiry_date)}
                        </p>
                      </div>

                      {/* Arrow */}
                      <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-1 transition-all mt-1" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && !selectedNotification && (
            <div className="px-5 py-3 border-t border-gray-200 bg-white">
              <p className="text-center text-sm text-gray-500">
                Showing {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Animation Styles */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default NotificationBell;
